import Bugsnag from '@bugsnag/js'
import axios from 'axios'

// lodash components
import isEmpty from 'lodash/isEmpty'
import includes from 'lodash/includes'

class CustomRequestError extends Error {
    constructor(response, ...params) {
        // Pass remaining arguments (including vendor specific ones) to parent constructor
        super(...params)

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor)
        }

        this.name = 'CustomRequestError'
        // Custom debugging information
        this.response = response
        // client-side error
        this.status = 0
    }
}

function logBugsnag(error) {
    if (window.BUGSNAG_VUE_API_KEY) {
        Bugsnag.notify(error)
        console.error(error)
    }
}

/**
 * This class makes it easy to fire and forget when fetching API data. If you need more control in error handling (e.g. form fields), use axios directly
 */
class ApiRequest {
    /**
     * Do an api call with the option of handling page errors. It will fail if any errors occur OR if the response is empty.
     *
     * @param {object} store
     * @param {object} axiosConfig
     * @param {bool} handleError
     */
    static async request(store, axiosConfig, handleError) {
        const promise = new Promise(async (resolve, reject) => {
            let errorResponse = null

            axiosConfig.headers = {
                ...axiosConfig.headers,
                'X-Requested-With': 'XMLHttpRequest',
            }

            try {
                const response = await axios(axiosConfig)

                if (response && !isEmpty(response.data)) {
                    resolve(response.data)
                    return
                }

                errorResponse = new CustomRequestError(response, 'missing data')

                logBugsnag(errorResponse)

                // .message on both types of errors
                reject(errorResponse)
            } catch (error) {
                if (!includes([404, 419, 422], error.response?.status)) {
                    logBugsnag(error)
                }

                if (error.response) {
                    errorResponse = error.response
                } else {
                    errorResponse = new CustomRequestError(null, 'unknown error')
                }

                reject(error)
            }

            if (handleError && store) {
                store.dispatch('processErrorHttpStatus', errorResponse.status)
            }
        })

        // handle error and return simple false if failure so we don't have to try catch on every api call
        if (handleError) {
            try {
                await promise
            } catch (error) {
                return false
            }
        }

        return promise
    }
}

// Provide aliases for supported request methods
const aliasNoDataMethods = ['delete', 'get', 'head', 'options']
aliasNoDataMethods.forEach(method => {
    ApiRequest[method] = function (store, methodUrl, params = {}, handleError = true) {
        let config = {
            method: method,
            url: methodUrl,
            params,
        }

        return this.request(store, config, handleError)
    }
})

const aliasWithDataMethods = ['post', 'put', 'patch']
aliasWithDataMethods.forEach(method => {
    ApiRequest[method] = function (store, methodUrl, data = {}, handleError = true) {
        return this.request(
            store,
            {
                method: method,
                url: methodUrl,
                data: data,
            },
            handleError,
        )
    }
})

export default ApiRequest
